import { put } from "redux-saga/effects";
import api from "../../../utils/apiService";
import { ENDPOINT } from "../../../config/endpoints";
import { EXTERNAL_LOYALTY_PARTNERS, METHODS } from "../../../data/constants";
import {
  getLoyaltyDetailsSuccess,
  getLoyaltyProvidersSuccess,
  getProgramDetailsSuccess,
  redeemPointsSuccess,
  verifyLoyaltyOtpSuccess,
} from "../../slices/Loyalty";
import { getDetailedCustomerInfo } from "../../slices/CustomerSlice";
import { notifyError } from "../../../effects-bridge";

/**
 * Uplifted from `redux-store/saga/loyalty` (apps/web + apps/mobile).
 *
 * A SPLIT, and it was the widest one in the saga phase: web had TWELVE watchers, mobile FOUR,
 * with only three workers in common.
 *
 * 🔴 **2026-09-05 — mobile took the `packages/core/loyalty` flow, and the split collapsed to
 * the watcher LISTS.** Every worker that flow needs is here now; the platform files hold only
 * a `root` that says which ones that app listens for.
 *
 * Shared: `commonPostData`, `getLoyaltyProvidersData`, `verifyOtpData` (identical from the
 * start), plus `getLoyaltyDetailsData` and `updateRedeemPointsData` (mobile converged onto
 * web's bodies — see each one's note), plus the five the flow's adapter dispatches:
 * `refundPoints`, `sendCustomerConsentOtpSaga`, `verifyCustomerConsentOtpSaga`,
 * `customerOptOutSaga`, `fetchProgramDetails`.
 *
 * The two consent-OTP workers had already moved here on 2026-09-04, when the kiosk's contact
 * flow (`ordering/customer`) became a cross-platform caller — the loyalty flow is the SECOND
 * caller to need them on both platforms, not the first. Both roots register them.
 *
 * 🔴 **2026-09-08 — `packages/core/discounts` took the COUPON/DEALS flow to both platforms,
 * and two more workers came with it:** `verifyExternalOffer` (an offer OTP on the offer-shaped
 * channel) and `unblockExternalPartnerDiscount` (releasing a coupon the partner blocked). Both
 * are reached from capability profiles now (`verifyChannel`, `requiresCouponUnblock`), so a
 * partner on either platform can need them, and their endpoints moved to the shared map with
 * them. Both roots register them.
 *
 * STILL WEB-ONLY: `fetchCustomerCoupon` alone. Its only caller is `components/XenoModal`, a web
 * POS surface, and it is not part of the discount flow — so its `customerCoupon` endpoint stays
 * in web's map too.
 *
 * ⚠️ Bare folder imports are REQUIRED in a `shared.ts` (hazard 16).
 *
 * ⚠️ `commonPostData` swallows every error and returns `undefined`, so the callers' `catch`
 * blocks below are effectively unreachable for request failures — the falsy-response branch is
 * what actually runs. Preserved verbatim on both platforms; it is long-standing behaviour that
 * `fetchProgramDetails`'s comment on web explicitly relies on.
 *
 * 🔴 **2026-09-08 — every `callback` here is now OPTIONAL, and the catches call
 * `actions.payload?.callback?.(...)`.** The apps had always dispatched these actions from a
 * surface that wanted the answer back, so `data.callback(response)` never had to be guarded.
 * `packages/core/loyalty` broke that: `fetchLoyaltyDetails(provider, cb?)` takes the callback as
 * OPTIONAL and `useLoyaltyRedemption`'s refetch effect passes none, because the panel reads the
 * balance out of the slice the worker already wrote. So `getLoyaltyDetailsData` threw on
 * `data.callback(...)`, fell into its own `catch`, and threw AGAIN on `actions.payload.data`
 * — the `.data.` typo, a payload key that has never existed — which took the loyalty saga tree
 * down with it (`takeEvery` propagates a worker throw to the root, cancelling all twelve
 * watchers, so redeem/verify/consent/refund all stopped answering for the session).
 *
 * The guards are the fix for both halves. Nothing that used to run stops running: `?.` only
 * changes the paths that previously threw. The same pair was fixed the same day in the two other
 * places it had been copied to — `saga/saleSummary/shared.ts` and `fetchCustomerCoupon`
 * (`index.desktop.ts`) — even though neither is reachable today, because "unreachable" is what
 * this one was until a new caller made the callback optional.
 */
export async function commonPostData(data: any) {
  try {
    const postApi = api.request(data);
    const fetchResponse = await postApi;
    return fetchResponse;
  } catch (e) {}
}

/** Identical in both apps. */
export function* getLoyaltyProvidersData(actions: any): Generator<any, void, any> {
  let url = ENDPOINT.LOYALTY.apiLoyaltyProvider();
  try {
    let dataToSend = {
      url: url,
    };
    let response = yield commonPostData(dataToSend);
    if (response && response?.providers?.length) {
      yield put(getLoyaltyProvidersSuccess(response));
    } else {
      yield put(getLoyaltyProvidersSuccess({ status: false, message: "Unable to fetch" }));
    }
  } catch (e) {
    yield put(
      getLoyaltyProvidersSuccess({
        code: "Something went wrong. Please try again later",
        status: false,
      }),
    );
  }
}

/** Identical in both apps. */
export function* verifyOtpData(actions: any): Generator<any, void, any> {
  let url = ENDPOINT.LOYALTY.apiVerifyOtp();
  try {
    let data = actions.payload;
    console.log("dfadf a ", actions.payload);
    let dataToSend = {
      url: url,
      method: "POST",
      data: JSON.stringify(data.postBody),
    };
    let response = yield commonPostData(dataToSend);
    if (response) {
      yield put(verifyLoyaltyOtpSuccess(response));
      data.callback?.(response);
    } else {
      yield put(verifyLoyaltyOtpSuccess(response));
      data.callback?.(response);
    }
  } catch (e) {
    yield put(
      verifyLoyaltyOtpSuccess({
        code: "Something went wrong. Please try again later",
        status: false,
      }),
    );
    console.log("check data ", e);
    actions.payload?.callback?.({
      code: "Something went wrong. Please try again later",
      status: false,
    });
  }
}

/* ── converged 2026-09-05: these two were the only per-platform WORKERS ─────────────── */

/**
 * 🔴 **Mobile's variant dispatched the RAW response** — no `providerId`, no `data` — while the
 * shared reducer writes `loyaltyDetails[payload.providerId] = payload.data`. It therefore wrote
 * `loyaltyDetails[undefined] = undefined` on every fetch, so no provider's balance was ever
 * readable and `packages/core/loyalty` would have held its panel at `hidden` forever. This body
 * (web's) is the correct one; mobile's was a latent bug its old UI hid by reading the raw slice.
 *
 * The falsy branch answers a ZEROED balance rather than nothing, which is what puts a failed
 * fetch in the flow's `ineligible` state instead of leaving it indistinguishable from loading.
 */
export function* getLoyaltyDetailsData(actions: any): Generator<any, void, any> {
  let url = ENDPOINT.LOYALTY.apiLoyaltyDetails();
  try {
    let data = actions.payload;
    console.log("dfadf a ", actions.payload);
    let dataToSend = {
      url: url,
      method: "POST",
      data: JSON.stringify(data.postBody),
    };
    let response = yield commonPostData(dataToSend);
    // Diverges from mobile, which dispatches the RAW response in both branches.
    if (response) {
      yield put(
        getLoyaltyDetailsSuccess({
          providerId: data.postBody?.providerId,
          data: { ...response },
        }),
      );
      data.callback?.(response);
    } else {
      yield put(
        getLoyaltyDetailsSuccess({
          providerId: data.postBody?.providerId,
          data: { redeemablePoints: 0, redeemableAmount: 0, currentPoints: 0 },
        }),
      );
      data.callback?.({ redeemablePoints: 0, redeemableAmount: 0, currentPoints: 0 });
    }
  } catch (e) {
    yield put(
      getLoyaltyDetailsSuccess({
        code: "Something went wrong. Please try again later",
        status: false,
      }),
    );
    console.log("check data ", e);
    actions.payload?.callback?.({
      code: "Something went wrong. Please try again later",
      status: false,
    });
  }
}

/**
 * Re-read the customer after a DotPe redeem / unblock call succeeds.
 *
 * 🔴 **Gated on the PROVIDER the call was made against, and that gate is the point.** Spending
 * DotPe points or releasing a DotPe coupon changes the customer's balance and milestone list at
 * the partner, so both are stale the moment the call returns — and those rows render straight
 * from `customerLoyaltyInfo`. A deal or coupon against any other provider changes neither, so it
 * must NOT pay for this extra round trip.
 *
 * ⚠️ **The gate reads `postBody.metaId`, not a caller-passed flag** (DM-146, 2026-09-11). It was
 * `data.isLoyaltyReward` until then — a boolean `DiscountDeals` set on the reward path and
 * `removeDiscountFromCart` threaded down through `removeExternalDiscount` purely to reach here.
 * Every one of those call sites already puts the partner's `metaId` in the post body it is
 * sending, so the flag was re-deriving, three layers up, something the payload already stated.
 * Two consequences worth knowing: the refetch now also fires for an ORDINARY DotPe coupon (not
 * just a milestone reward), and it no longer fires for a reward redeemed against a non-DotPe
 * partner — which is what "the customer record that went stale is DotPe's" actually means.
 *
 * `external: false` — `customerRefId` is our own id, not the partner's.
 *
 * Shared rather than web-only: the three workers that call it are shared now, and mobile simply
 * never dispatches a DotPe redeem/unblock today, so this is a no-op there until it does.
 */
function* refreshCustomerAfterLoyaltyReward(data: any): Generator<any, void, any> {
  if (data?.postBody?.metaId !== EXTERNAL_LOYALTY_PARTNERS.DOTPE || !data?.customerRefId) return;
  yield put(getDetailedCustomerInfo({ ref: data.customerRefId, external: false }));
}

/**
 * ⚠️ Mobile neither toasted `errorCode` nor branched on it before 2026-09-05. It does now: the
 * flow surfaces the same failure on the panel, and the doubled feedback is web's long-standing,
 * deliberate behaviour (see `components/Loyalty`'s note on it).
 */
export function* updateRedeemPointsData(actions: any): Generator<any, void, any> {
  let url = ENDPOINT.LOYALTY.apiLoyaltyRedeem();
  try {
    let data = actions.payload || {};
    let dataToSend = {
      url: url,
      method: "POST",
      data: JSON.stringify(data.postBody),
    };
    let response = yield commonPostData(dataToSend);
    // WEB ONLY — mobile neither toasts nor branches on errorCode.
    if (response?.errorCode) {
      notifyError(response?.errorCode);
    }
    if (response && !response?.errorCode) {
      yield put(redeemPointsSuccess(response));
      yield* refreshCustomerAfterLoyaltyReward(data);
      data?.callback?.(response);
    } else {
      yield put(redeemPointsSuccess(response));
      data?.callback?.(response);
    }
  } catch (e) {
    yield put(
      redeemPointsSuccess({
        code: "Something went wrong. Please try again later",
        status: false,
      }),
    );
    console.log("check data ", e);
    actions.payload?.callback?.({
      code: "Something went wrong. Please try again later",
      status: false,
    });
  }
}

/* ── the five the loyalty flow's adapter dispatches ──────────────────────────────── */

export function* refundPoints(actions: any): Generator<any, void, any> {
  let url = ENDPOINT.LOYALTY.loyaltyRefund();
  try {
    let data = actions.payload;
    let dataToSend = {
      url: url,
      method: "POST",
      data: JSON.stringify(data.postBody),
    };
    let response = yield commonPostData(dataToSend);
    if (response) {
      //yield put(verifyLoyaltyOtpSuccess(response));refund success
      data.callback?.(response);
    } else {
      //yield put(verifyLoyaltyOtpSuccess(response)); //refund success
      data.callback?.(response);
    }
  } catch (e) {
    actions.payload?.callback?.({
      code: "Something went wrong. Please try again later",
      status: false,
    });
  }
}

/**
 * Send the customer-consent OTP (`customer/consent-otp`).
 *
 * ⚠️ The response is a THREE-way answer, not a boolean — `{ status, otpSent, message }`, and
 * what a bare `status` MEANS depends on which path asked. `packages/core/loyalty/consent.ts` is
 * the one place that reading lives, in two classifiers:
 * `classifyConsentOtpResponse` (a code was requested → bare status means it was sent) and
 * `classifyEnrolWithoutOtpResponse` (this call was the enrolment → bare status means enrolled,
 * and `otpSent` overrules it). Every caller — the POS's customer modal and the kiosk's contact
 * flow — goes through those; neither reads this response itself.
 */
export function* sendCustomerConsentOtpSaga(actions: any): Generator<any, void, any> {
  let url = ENDPOINT.LOYALTY.sendCustomerConsentOtp();
  const data = actions.payload;
  try {
    let dataToSend = {
      url: url,
      method: METHODS.POST,
      data: JSON.stringify(data.dataToPost),
    };
    let response = yield commonPostData(dataToSend);
    data.callback?.(response);
  } catch (e) {
    console.log("check data ", e);
    data.callback?.({ code: "Something went wrong. Please try again later", status: false });
  }
}

/**
 * Verify that OTP (`customer/consent-otp/verify`). Moved and registered with its sibling above,
 * for the same reason — the two are one flow and splitting them across platforms stopped being
 * an option once either had a cross-platform caller.
 */
export function* verifyCustomerConsentOtpSaga(actions: any): Generator<any, void, any> {
  let url = ENDPOINT.LOYALTY.verifyCustomerConsentOtp();
  const data = actions.payload;
  try {
    let dataToSend = {
      url: url,
      method: METHODS.POST,
      data: JSON.stringify(data.dataToPost),
    };
    let response = yield commonPostData(dataToSend);
    data.callback?.(response);
  } catch (e) {
    console.log("check data ", e);
    data.callback?.({ code: "Something went wrong. Please try again later", status: false });
  }
}


export function* customerOptOutSaga(actions: any): Generator<any, void, any> {
  const url = ENDPOINT.LOYALTY.customerOptOut();
  const data = actions.payload;
  try {
    const dataToSend = {
      url,
      method: METHODS.POST,
      data: JSON.stringify(data.dataToPost),
    };
    const response = yield commonPostData(dataToSend);
    data.callback?.(response);
  } catch (e) {
    data.callback?.({ status: false });
  }
}


// stable reference so repeated negative responses don't churn useSelector subscribers
const EMPTY_PROGRAM_DETAILS = {};

export function* fetchProgramDetails(actions: any): Generator<any, void, any> {
  const { channelDefId } = actions.payload;
  const url = ENDPOINT.LOYALTY.getProgramDetails(channelDefId);
  try {
    const response = yield commonPostData({ url });
    // A negative envelope means BE has no active program for this channel. It must overwrite the
    // cached entry, otherwise a previously fetched data.status:true keeps the loyalty consent toggle
    // visible for the rest of the session (programDetailsMap has no per-channel invalidation).
    // response is undefined only when the request itself failed (commonPostData swallows errors) —
    // keep the last known value then instead of wiping it on a transient network failure.
    // `profileConfig` can arrive on a negative envelope; keep it so the profile card can read it.
    if (response)
      yield put(
        getProgramDetailsSuccess({
          channelDefId,
          data: response.status
            ? response
            : response.profileConfig
              ? { profileConfig: response.profileConfig }
              : EMPTY_PROGRAM_DETAILS,
        }),
      );
  } catch (e) {
    console.log("fetchProgramDetails error", e);
  }
}


/* ── the coupon/deals partner conversation ─────────────────────────────────────────────── */

/**
 * Verify an offer OTP on the OFFER-shaped channel (`sale/customer/offer/redeem`).
 *
 * The other channel is `updateRedeemPointsData` above — `redeemPoints`, the loyalty endpoint,
 * which a partner speaking the loyalty envelope uses for its coupons too. Which one a partner
 * gets is `getVerifyChannel(metaId)`, not a call-site branch; see
 * `packages/core/promotions/partners/steps.ts`.
 *
 * ⚠️ MOVED here from `index.desktop.ts` on 2026-09-08, body verbatim. It was web-only because
 * only web had a coupon surface, not because it has a platform half.
 */
export function* verifyExternalOffer(actions: any): Generator<any, void, any> {
  const url = ENDPOINT.LOYALTY.apiOfferRedeem();
  const data = actions.payload || {};
  try {
    const dataToSend = {
      url,
      method: "POST",
      data: JSON.stringify(data.postBody),
    };
    const response = yield commonPostData(dataToSend);
    if (response?.errorCode) {
      notifyError(response?.errorCode);
    }
    if (response && !response?.errorCode) {
      yield* refreshCustomerAfterLoyaltyReward(data);
      data.callback?.(response);
    } else {
      yield put(redeemPointsSuccess(response));
      data.callback?.(response);
    }
  } catch (e) {
    yield put(
      redeemPointsSuccess({
        code: "Something went wrong. Please try again later",
        status: false,
      }),
    );
    console.log("check data ", e);
    data.callback?.({ code: "Something went wrong. Please try again later", status: false });
  }
}

/**
 * Release a coupon the partner has blocked against this customer.
 *
 * ⚠️ MOVED here from `index.desktop.ts` on 2026-09-08, body verbatim. Gated by
 * `requiresCouponUnblock(metaId)` — a partner that blocks nothing never dispatches it.
 *
 * ⚠️ The `catch` used to read `actions.payload.data.callback` — the `.data.` typo, a payload key
 * that has never existed, so the error path threw instead of reporting. Fixed with the rest of
 * this file's callback handling on 2026-09-08; see the header.
 */
export function* unblockExternalPartnerDiscount(actions: any): Generator<any, void, any> {
  let url = ENDPOINT.LOYALTY.unBlockDiscount();
  try {
    let data = actions.payload;
    let dataToSend = {
      url: url,
      method: "POST",
      data: JSON.stringify(data.postBody),
    };
    let response = yield commonPostData(dataToSend);
    if (response?.errorCode) {
      notifyError(response?.errorCode);
    }
    if (response && !response?.errorCode) {
      yield* refreshCustomerAfterLoyaltyReward(data);
      data.callback?.(response);
    } else {
      yield put(redeemPointsSuccess(response));
      data.callback?.(response);
    }
  } catch (e) {
    yield put(
      redeemPointsSuccess({
        code: "Something went wrong. Please try again later",
        status: false,
      }),
    );
    console.log("check data ", e);
    actions.payload?.callback?.({
      code: "Something went wrong. Please try again later",
      status: false,
    });
  }
}
